/* eslint-disable max-params */
import { inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  getFileNameTimeStamp,
  getTimeStamp,
  getUserTimezoneIdentifier,
  UtilsService,
} from './utils.service';
import { Store } from '@ngrx/store';
import { HttpParams } from '@angular/common/http';
import { catchError, EMPTY, of, Subject, switchMap, take } from 'rxjs';
import { selectUser } from '@shared/store/selectors/user.selectors';
import saveAs from 'file-saver';
import {
  CheckboxCellConfig,
  ComponentCellType,
  HeaderCellConfiguration,
  HeaderCheckboxCellConfig,
  WcgAdvancedFilter,
  WcgAdvancedFilterOptions,
  WcgMenuOption,
  WcgTableData,
  WcgTableFilters,
  WcgTableRow,
  WcgTableSortBy,
} from '@wcg/frontend-framework';

import { cloneDeep } from 'lodash';
import { GRID_VIEW_DATA_ACTIONS } from '@data-access/grid-views';
import { moveItemInArray } from '@angular/cdk/drag-drop';
import { PageEvent } from '@angular/material/paginator';
import { AdditionalHeader } from '@shared/types/additional-header.type';
import {
  Filter,
  sortParam,
  WcgFilterIndicatorsOptions,
  WcgTableValues,
} from '@shared/interfaces/wcg-table-utils.interface';
import { LoadingService } from '@loader/spinner';

import environment from '@environments/environment';
import { DocumentTypeFilter } from '@documents/models';
import {
  createExportExcelGridWorker,
  createExportPdfGridWorker,
} from './service-worker.util';
import moment, { Moment } from 'moment';

@Injectable({ providedIn: 'root' })
export class WcgTableUtilsService {
  private readonly _utilsService = inject(UtilsService);
  private readonly _store = inject(Store);
  private readonly _snackbar = inject(MatSnackBar);
  private readonly _loadingService = inject(LoadingService);

  private workerExcel?: Worker;
  private workerPdf?: Worker;

  constructor() {
    if (typeof Worker !== 'undefined') {
      this.workerExcel = createExportExcelGridWorker() as Worker;
      this.workerPdf = createExportPdfGridWorker() as Worker;
    }
  }

  generateTableQueryParams(
    tableValues: WcgTableValues,
    additionalParams?: Record<string, unknown>,
  ): HttpParams {
    let queryParams = new HttpParams()
      .set('timezone', this._utilsService.getUserTimezoneOffset())
      .set('timezone_identifier', getUserTimezoneIdentifier())
      .set('page', (tableValues?.page || 1).toString());

    if (tableValues?.searchValue) {
      queryParams = queryParams.set('query', tableValues.searchValue);
    }

    if (!additionalParams?.page_size) {
      queryParams = queryParams.set(
        'page_size',
        (tableValues?.pageSize || 100).toString(),
      );
    }

    if (
      tableValues?.sortParam?.field &&
      tableValues?.sortParam?.dir &&
      tableValues.sortParam.field !== 'selectable'
    ) {
      queryParams = this.appendSortParams(queryParams, tableValues.sortParam);
    }

    if (Array.isArray(tableValues?.filters) && tableValues.filters.length) {
      queryParams = this.appendFilterParams(queryParams, tableValues.filters);
    }

    const excludedKeys = new Set([
      'page',
      'pageSize',
      'searchValue',
      'sortParam',
      'filters',
    ]);

    if (tableValues) {
      Object.entries(tableValues).forEach(([key, value]) => {
        if (!excludedKeys.has(key) && value)
          queryParams = queryParams.set(key, value.toString());
      });
    }
    if (additionalParams) {
      Object.entries(additionalParams).forEach(([key, value]) => {
        if (value) queryParams = queryParams.set(key, value.toString());
      });
    }

    return queryParams;
  }

  private appendSortParams(
    params: HttpParams,
    sortParam: sortParam,
  ): HttpParams {
    const columnMapping = sortParam.field;
    return params
      .append('sort_by_field', columnMapping)
      .append('sort_by_order', sortParam.dir || 'asc');
  }

  private appendFilterParams(
    params: HttpParams,
    filters: Filter[] | WcgAdvancedFilter[],
  ): HttpParams {
    const cFilters = filters
      .filter((filter: any) => {
        const key = Object.keys(filter)[0];
        const item = filter[key];

        if (!item) return false;

        const hasSingleValue =
          item.value?.value !== undefined && item.value?.value !== '';
        const hasValueArray =
          (Array.isArray(item.values) && item.values?.length > 0) ||
          (Array.isArray(item.value) && item.value?.length > 0);
        const hasValueLength =
          typeof item.value === 'string' && item.value?.length > 0;
        const hasDate = !!item.date;
        const hasRange = item.start && item.end;

        return (
          hasSingleValue ||
          hasValueArray ||
          hasValueLength ||
          hasDate ||
          hasRange
        );
      })
      .map((filter: any) => {
        const cFilter = filter[Object.keys(filter)[0]];
        const filterParams: Filter = {
          type: this.getFilterType(cFilter.type),
          column: cFilter.columnKey,
          values:
            this.getFilterValues(cFilter.value) ??
            this.getFilterValues(cFilter.values),
          valuesLabels:
            this.getFilterValuesLables(cFilter.value) ??
            this.getFilterValuesLables(cFilter.values),
          label: cFilter.label,
        };

        if (cFilter.start && cFilter.end) {
          const startMoment: Moment = moment.isMoment(cFilter.start)
            ? cFilter.start
            : moment.utc(cFilter.start);
          filterParams.start_date = startMoment.startOf('day').toISOString();

          const endMoment: Moment = moment.isMoment(cFilter.end)
            ? cFilter.end
            : moment.utc(cFilter.end);
          filterParams.end_date = endMoment.endOf('day').toISOString();
        }

        return filterParams;
      });

    params = params.append('grid_view', 'true');
    if (cFilters.length > 0) {
      // ERM-3701, ERM-3711 = Don't encode an empty set of filters
      params = params.append(
        'filters',
        this.encodeBase64(JSON.stringify(cFilters)),
      );
    }
    return params;
  }

  private getFilterType(value: string): string {
    return value?.replace(/-/g, '_');
  }

  private getFilterValues(values: unknown): string[] | undefined {
    if (Array.isArray(values)) {
      return values.map((v) => {
        if (typeof v === 'object' && v !== null) {
          if ('value' in v || 'id' in v) {
            return `${v.value ?? v.id}`;
          }
        }
        return `${v}`;
      });
    }

    if (typeof values === 'object' && values !== null && 'value' in values) {
      return [String((values as { value: unknown }).value)];
    }

    return values !== null && values !== undefined
      ? [String(values)]
      : undefined;
  }

  private getFilterValuesLables(values: unknown): string[] | undefined {
    if (Array.isArray(values)) {
      return values.map((v) =>
        typeof v === 'object' && v !== null && 'label' in v
          ? String((v as { label: unknown }).label)
          : String(v),
      );
    }

    if (typeof values === 'object' && values !== null && 'label' in values) {
      return [String((values as { label: unknown }).label)];
    }

    return values !== null && values !== undefined
      ? [String(values)]
      : undefined;
  }

  private encodeBase64(str: string): string {
    try {
      // First convert the string to UTF-8 bytes
      const utf8Bytes = new TextEncoder().encode(str);
      // Convert bytes to binary string that btoa can handle
      const binaryString = Array.from(utf8Bytes)
        .map((byte) => String.fromCharCode(byte))
        .join('');
      // Encode to base64
      return btoa(binaryString);
    } catch (e) {
      console.error('Error encoding string to base64:', e);
      return '';
    }
  }

  exportStarted() {
    this.showSnackbar('Exporting data...', 'snackbar-loading');
  }

  exportData(
    tableData: unknown[],
    queryParams: HttpParams,
    columnsConfig: unknown[],
    fileName: string,
    exportType: string,
    additionalHeaderData: AdditionalHeader[] = [],
  ) {
    const data = this.normalizeData(tableData);
    const exludeColumns = ['Actions', 'selectable', 'Chat', 'Delete', ' '];
    columnsConfig = columnsConfig
      .map((col: any) => ({
        ...col,
        title: col.cellData ?? '',
        field: (col.columnKey ?? col.cellData ?? '')
          .replace(/[\s.]+/g, '_')
          .toLowerCase(),
      }))
      .filter(
        (col: any) =>
          !exludeColumns.includes(col.field) &&
          !exludeColumns.includes(col.title),
      );

    queryParams = this.convertHttpParamsToObject(queryParams);

    switch (exportType.toLowerCase()) {
      case 'csv':
        this.exportToCSV(
          data,
          queryParams,
          columnsConfig,
          additionalHeaderData,
        ).subscribe((data) => {
          this.saveCSVFile(data ?? '', fileName);
        });
        break;
      case 'pdf':
        this.exportToPdf(
          data,
          queryParams,
          columnsConfig,
          fileName,
          additionalHeaderData,
        );
        break;
      default:
        this.exportToCSV(
          data,
          queryParams,
          columnsConfig,
          additionalHeaderData,
        ).subscribe((data) => {
          this.saveCSVFile(data ?? '', fileName);
        });
    }
  }
  exportToCSV(
    data: unknown[],
    queryParams: HttpParams,
    columnsConfig: unknown[],
    additionalHeaderData: AdditionalHeader[],
  ): Subject<string> {
    const csvSubject = new Subject<string>();

    this._store
      .select(selectUser)
      .pipe(
        take(1),
        switchMap((user) => {
          if (!this.workerExcel) {
            console.error('Web Worker not available.');
            this.showSnackbar(
              'Error: Web Worker not available.',
              'snackbar-error',
            );
            return of('');
          }

          this.workerExcel.onmessage = ({ data }) => {
            if (data.error) {
              console.error('Error in workerExcel:', data.error);
              this.showSnackbar('Error exporting data.', 'snackbar-error');
              csvSubject.next('');
            } else {
              csvSubject.next(data);
              this.showSnackbar('Exporting data complete.', 'snackbar-success');
            }
            csvSubject.complete();
          };
          const timeStamp = getTimeStamp('DD-MMM-YYYY HH:mm:ss');
          let headerDetails = 'Created from WCG eReview Manager\n';
          headerDetails += `Generated by ${user?.first_name} ${user?.last_name} (${user?.email})\n`;
          headerDetails += `Generated ${timeStamp}`;

          if (additionalHeaderData?.length > 0) {
            const additionalHeaderDataLength = additionalHeaderData.length - 1;
            headerDetails += `\n\n`;
            additionalHeaderData.forEach((item, index) => {
              if (item.label && item.value) {
                headerDetails += `${item.label} : ${item.value}${
                  index !== additionalHeaderDataLength ? '\n' : ''
                }`;
              }
            });
          }

          // Start the workerExcel with all the data
          if (Array.isArray(data)) {
            data = data.map((row: any) => {
              if (row.qc_function) {
                row.name = row.name?.cellData === '' ? 'Unassigned' : row.name;
              }
              return row;
            });
          }
          this.workerExcel.postMessage({
            data,
            columnsConfig,
            queryParams,
            headerDetails,
          });

          return csvSubject;
        }),
        catchError((err) => {
          console.error('Error in exportToCSV:', err);
          this.showSnackbar('Error occurred during export.', 'snackbar-error');
          csvSubject.next('');
          csvSubject.complete();
          return of(''); // Return an observable with an empty string as error state
        }),
      )
      .subscribe();
    return csvSubject;
  }

  saveCSVFile(csv: string, fileName: string): void {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const timeStamp = getFileNameTimeStamp().replace(/\+/g, '_');

    fileName = `${fileName.replace(/ /g, '_')}_Export_`;
    saveAs(blob, `${fileName}${timeStamp}.csv`);
  }

  convertHttpParamsToObject(queryParams: HttpParams): any {
    const query = queryParams.get('query') || '';
    const page = queryParams.get('page')
      ? parseInt(queryParams.get('page') as string, 10)
      : 1;
    const pageSize = queryParams.get('page_size')
      ? parseInt(queryParams.get('page_size') as string, 10)
      : `10`;
    const filters = queryParams.get('filters')
      ? queryParams.get('filters')
      : [];

    const sort_by_field = queryParams.get('sort_by_field')
      ? queryParams.get('sort_by_field')
      : '';

    const sort_by_order = queryParams.get('sort_by_order')
      ? queryParams.get('sort_by_order')
      : '';

    return {
      query,
      page,
      page_size: pageSize,
      filters,
      sort_by_field,
      sort_by_order,
    };
  }

  getCheckboxHeader(disabled = false) {
    const checkboxConfig: HeaderCheckboxCellConfig = {
      rowIndex: -1,
      selectAllOption: true,
      checked: false,
      indeterminate: false,
      sticky: true,
    };
    const checkboxHeader = {
      cellData: 'selectable',
      componentConfig: {
        ...checkboxConfig,
        sortable: false,
        showColumn: true,
        maxWidth: 10,
        disabled,
      },
      componentType: ComponentCellType.HeaderCheckbox,
    };
    return checkboxHeader;
  }
  getCheckboxCell(
    index: number,
    item: unknown,
    selectedRows: unknown[],
    disabled = false,
  ) {
    const checkboxConfig: CheckboxCellConfig = {
      rowIndex: index,
      checked: selectedRows.some((row: unknown) => {
        const rowDataItem = (
          row as { dataItem?: { cellData?: { id: string } } }
        ).dataItem;
        return rowDataItem?.cellData?.id === (item as { id: string }).id;
      }),
    };
    return {
      selectable: {
        componentType: ComponentCellType.Checkbox,
        cellData: '',
        componentConfig: {
          ...checkboxConfig,
          sortable: false,
          disabled,
        },
      },
    };
  }

  normalizeData(data: unknown[]) {
    return data.map((item: any) => {
      return Object.keys(item).reduce((acc: any, key) => {
        const formattedKey = key.includes(' ')
          ? key.replace(/ /g, '_').toLowerCase()
          : key.includes('.')
            ? key.replace(/\./g, '_')
            : key;

        const sanitizedtext = this.sanitizeText(item[key].cellData ?? '');
        acc[formattedKey] = sanitizedtext;
        return acc;
      }, {});
    });
  }

  sanitizeText = (text: string | null | undefined): string =>
    text
      ? String(text)
          .replace(/[\u200B-\u200D\uFEFF\u202F]/g, ' ')
          .trim()
      : '';

  mapToCellColumn(item: any, headerConfig: any) {
    return {
      componentType: 'default',
      cellData: item[headerConfig.componentConfig.field],
    };
  }

  handleSelection(event: any, selectedRows: any[]): any[] {
    const index = selectedRows.findIndex(
      (item: any) => item.dataItem.cellData.id === event.dataItem.cellData.id,
    );

    if (event.checked) {
      if (index === -1) {
        return [...selectedRows, event];
      }
    } else {
      if (index > -1) {
        return selectedRows.filter(
          (item: any) =>
            item.dataItem.cellData.id !== event.dataItem.cellData.id,
        );
      }
    }

    return selectedRows;
  }

  handleSelectionAll(
    event: any,
    selectedRows: any[],
    tableDataRows: any[],
  ): any[] {
    if (event.checked) {
      tableDataRows.forEach((row: any) => {
        const exists = selectedRows.some(
          (selectedRow: any) =>
            selectedRow.dataItem.cellData.id === row.dataItem.cellData.id,
        );
        if (!exists) {
          selectedRows.push(row);
        }
      });
    } else {
      selectedRows = selectedRows.filter(
        (selectedRow: any) =>
          !tableDataRows.some(
            (tableRow: any) =>
              tableRow.dataItem.cellData.id ===
              selectedRow.dataItem.cellData.id,
          ),
      );
    }
    return selectedRows;
  }

  updateHeaderChecked(
    tableData: WcgTableData,
    selectedRows: any[],
  ): WcgTableData {
    const selectedCount = tableData.rows?.reduce((count, item) => {
      if (
        selectedRows.some(
          (row) =>
            row.dataItem?.cellData?.id === item.id ||
            row.dataItem?.cellData?.id === item.dataItem?.cellData?.id,
        )
      ) {
        count++;
      }
      return count;
    }, 0);

    const totalCount = tableData.rows?.length;

    const header = {
      ...tableData?.headers[0],
      componentConfig: {
        ...tableData?.headers[0]?.componentConfig,
        checked: selectedCount > 0 && selectedCount === totalCount,
        indeterminate: selectedCount > 0 && selectedCount !== totalCount,
      },
    };

    const headers = [header];

    if (tableData?.headers.length > 1) {
      headers.push(
        ...(tableData!.headers.slice(1) as {
          cellData: string;
          componentConfig: any;
          componentType: string;
        }[]),
      );
    }

    return { ...tableData, headers: headers, selectedRows: selectedRows };
  }

  updateRowsChecked(
    tableData: WcgTableData,
    selectedRows: any[],
    checked: boolean,
  ): WcgTableData {
    return {
      ...tableData,
      rows: tableData.rows?.map((row: any) => ({
        ...row,
        selectable: {
          ...row.selectable,
          componentConfig: {
            ...row.selectable?.componentConfig,
            checked: selectedRows.some(
              (selected) =>
                selected.dataItem.cellData.id === row.dataItem.cellData.id,
            )
              ? checked
              : false,
          },
        },
      })),
      selectedRows: selectedRows,
    };
  }

  handleTableSelection(
    headerChecked: boolean,
    rowChecked: boolean,
    tableData: WcgTableData,
    selectedRows: any[],
    checked = true,
  ) {
    if (headerChecked) {
      tableData = this.updateHeaderChecked(tableData, selectedRows);
    }
    if (rowChecked) {
      tableData = this.updateRowsChecked(tableData, selectedRows, checked);
    }
    return tableData;
  }

  exportToPdf(
    data: unknown[],
    queryParams: HttpParams,
    columnsConfig: unknown[],
    fileName: string,
    additionalHeaderData: AdditionalHeader[],
  ) {
    this._store
      .select(selectUser)
      .pipe(
        take(1),
        switchMap((user: any) => {
          if (!this.workerPdf) {
            console.error('Web Worker not available.');
            this.showSnackbar(
              'Error: Web Worker not available.',
              'snackbar-error',
            );
            return EMPTY;
          }

          this.workerPdf.onmessage = ({ data }) => {
            this.showSnackbar('Exporting data complete.', 'snackbar-success');
            this.downloadPDF(data, fileName);
          };

          const timeStamp = getTimeStamp('DD-MMM-YYYY HH:mm:ss');

          // Prepare header details with user info
          const headerDetails: any = [
            {
              text: 'Created from WCG eReview Manager',
              fontSize: 18,
              bold: true,
              margin: [0, 0, 0, 10],
            },
            {
              text: `Generated by ${user?.first_name} ${user?.last_name} (${user?.email})`,
              fontSize: 12,
              margin: [0, 0, 0, 10],
            },
            {
              text: `Generated ${timeStamp}`,
              fontSize: 12,
              margin: [0, 0, 0, 20],
            },
          ];

          if (additionalHeaderData?.length > 0) {
            additionalHeaderData.forEach((item) => {
              if (item.label && item.value) {
                headerDetails.push({
                  text: `${item.label}: ${item.value}`,
                  fontSize: 12,
                  margin: [0, 0, 0, 5],
                });
              }
            });
          }

          // Ensure data is in an array and handle specific adjustments for data rows
          if (Array.isArray(data)) {
            data = data.map((row: any) => {
              if (row.qc_function) {
                row.name = row.name?.cellData === '' ? 'Unassigned' : row.name;
              }
              return row;
            });
          }

          // Start the Web Worker with necessary data
          this.workerPdf.postMessage({
            data,
            columnsConfig,
            queryParams,
            headerDetails,
          });

          return EMPTY; // Always return an observable (even if it's empty)
        }),
        catchError((err) => {
          console.error('Error in exportToPDF:', err);
          this.showSnackbar('Error occurred during export.', 'snackbar-error');
          return EMPTY; // Return EMPTY in case of an error
        }),
      )
      .subscribe();
  }

  private downloadPDF(base64: string, fileName: any) {
    const timeStamp = getFileNameTimeStamp().replace(/\+/g, '_');
    fileName = `${fileName.replace(/ /g, '_')}_Export_${timeStamp}.pdf`;
    const link = document.createElement('a');
    link.href = `data:application/pdf;base64,${base64}`;
    link.download = fileName;
    link.click();
  }

  private showSnackbar(message: string, messageClass: string): void {
    this._snackbar.open(message, 'Close', {
      panelClass: messageClass,
      duration: 5000,
    });
  }

  filterData(conditions: WcgAdvancedFilter[], data: any[]): any[] {
    if (!Array.isArray(conditions)) return data;
    return data.filter((item) =>
      conditions.every((condition) => this.applyCondition(item, condition)),
    );
  }

  private applyCondition(item: any, condition: WcgAdvancedFilter): boolean {
    const filterMap = new Map(Object.entries(condition));

    for (const [_, filterOption] of filterMap) {
      if (
        !filterOption ||
        !filterOption.columnKey ||
        (!filterOption.value && !filterOption.formatted) ||
        (Array.isArray(filterOption.value) &&
          filterOption.value.length === 0) ||
        filterOption.formatted == '-' ||
        !Object.hasOwn(item, filterOption.columnKey)
      ) {
        return true;
      }

      const itemValue = item[filterOption.columnKey];

      switch (this._removeDashesAndUnderscores(filterOption.type!)) {
        case 'string':
          if (typeof itemValue === 'string') {
            return itemValue
              .toLowerCase()
              .includes((filterOption.value as string).toLowerCase());
          }
          return false;

        case 'daterange': {
          const itemDate = new Date(itemValue);
          const startDate = new Date(filterOption.start || '');
          const endDate = new Date(filterOption.end || '');

          if (isNaN(itemDate.getTime())) {
            return false;
          }

          const itemDateOnly = new Date(
            itemDate.getFullYear(),
            itemDate.getMonth(),
            itemDate.getDate(),
          );
          const startDateOnly = new Date(
            startDate.getFullYear(),
            startDate.getMonth(),
            startDate.getDate(),
          );
          const endDateOnly = new Date(
            endDate.getFullYear(),
            endDate.getMonth(),
            endDate.getDate(),
          );

          return itemDateOnly >= startDateOnly && itemDateOnly <= endDateOnly;
        }
        case 'singleselect':
        case 'multiselect': {
          if (Array.isArray(filterOption.value)) {
            return (filterOption.value as WcgMenuOption[]).some(
              (item: WcgMenuOption) =>
                item === itemValue || item.value === itemValue,
            );
          } else if (
            filterOption.value &&
            typeof filterOption.value === 'object'
          ) {
            return (filterOption.value as WcgMenuOption).label === itemValue;
          }
          return false;
        }

        default:
          if (typeof itemValue === 'string') {
            return itemValue
              .toLowerCase()
              .includes((filterOption.value as string).toLowerCase());
          }
          return false;
      }
    }

    return false;
  }

  /**
   * Apply filters to table data. This function does not mutate the tableData object, it returns a new object with the filters applied.
   * @param tableData - WcgTableData
   * @param filters - WcgTableFilters
   * @returns WcgTableData
   */
  applyFiltersToTableData(tableData: WcgTableData, filters: WcgTableFilters) {
    const nextTableData = cloneDeep(tableData);

    if (!nextTableData.filters) {
      nextTableData.filters = { searchString: '', advancedFilters: {} };
    }

    if (filters.searchString) {
      nextTableData.filters.searchString = filters.searchString;
    }

    if (filters.advancedFilters && Array.isArray(filters.advancedFilters)) {
      filters.advancedFilters.forEach((filter: any) => {
        if (!nextTableData.filters?.advancedFilters?.[filter.column]) {
          return;
        }

        switch (filter.type) {
          case 'date-range':
          case 'date_range':
            nextTableData.filters.advancedFilters[filter.column] = {
              ...nextTableData.filters.advancedFilters[filter.column],
              column: filter.values?.columnKey,
              ...filter.values,
            };
            break;

          default:
            nextTableData.filters.advancedFilters[filter.column].value =
              filter.values;
            break;
        }
      });
    }

    this._store.dispatch(
      GRID_VIEW_DATA_ACTIONS.setCustomFilters({
        filters: nextTableData.filters,
        sortBy: tableData.sortBy!,
      }),
    );

    return nextTableData;
  }

  // TODO: This functionality is duplicated from frontend-framework.
  // This should be removed.
  updateColumnSortState(
    columns: HeaderCellConfiguration[],
    event: WcgTableSortBy,
  ): HeaderCellConfiguration[] {
    if (!event) {
      return columns;
    }

    return columns.map((column) => {
      const columnKey =
        column.columnKey ||
        column.componentConfig?.field ||
        column.cellData.toLowerCase().replace(/ /g, '_');

      if (columnKey === event.headerKey) {
        return {
          ...column,
          componentConfig: {
            ...column.componentConfig,
            isAsc: event.isAsc,
            sortDirection: event.isAsc
              ? 'asc'
              : event.isAsc !== null &&
                  event.isAsc !== undefined &&
                  !event.isAsc
                ? 'desc'
                : null,
          },
        };
      }
      // Reset sort direction for other columns
      return {
        ...column,
        componentConfig: {
          ...column.componentConfig,
          isAsc: null,
          sortDirection: null,
        },
      };
    });
  }

  updateSortParam(
    event: any,
    columns: HeaderCellConfiguration[],
    tableData?: WcgTableData,
  ): sortParam | null {
    if (!event || event.isAsc === undefined) return null;
    const column = this.findColumn(event.headerKey, columns);
    if (!column) return null;

    const direction = column.componentConfig?.sortDirection || 'asc';
    const field =
      column.columnKey || column.componentConfig?.field || event.headerKey;

    if (tableData) {
      this._store.dispatch(
        GRID_VIEW_DATA_ACTIONS.setCustomFilters({
          filters: tableData?.filters,
          sortBy: { field, dir: direction },
        }),
      );
    }

    return {
      field,
      dir: direction,
      // @ts-expect-error - This is a valid type
      isAsc: direction === 'asc' ? true : direction === 'desc' ? false : null,
    };
  }

  private findColumn(
    headerKey: string,
    columns: HeaderCellConfiguration[],
  ): HeaderCellConfiguration | undefined {
    return columns?.find((column) => {
      const columnField = column.componentConfig?.field;
      const columnKey = column.columnKey;

      return (
        columnField === headerKey ||
        columnKey === headerKey ||
        column.cellData.toLowerCase().replace(/ /g, '_') ===
          headerKey.toLowerCase()
      );
    });
  }

  mapResponseToWcgTableData(
    data: WcgTableRow[],
    ignoreHeaderMapping = false,
    tableData: WcgTableData,
    searchValue: string,
    defaultColumns: HeaderCellConfiguration[],
  ): WcgTableData {
    const updatedTableData = {
      ...tableData,
      rows: data,
      filters: { ...tableData.filters, searchString: searchValue },
    };

    if (!ignoreHeaderMapping) {
      const defaultHeaders = cloneDeep(defaultColumns);
      defaultHeaders.forEach((header: HeaderCellConfiguration) => {
        const currentHeader = updatedTableData.headers?.find(
          (h: HeaderCellConfiguration) => h.columnKey === header.columnKey,
        );
        if (currentHeader) {
          header.cellData = currentHeader.cellData;
          header.componentConfig = currentHeader.componentConfig;
          header.componentType = currentHeader.componentType;
          header.columnKey = currentHeader.columnKey;
        }
      });

      return { ...updatedTableData, headers: cloneDeep(defaultHeaders) };
    }

    return updatedTableData;
  }

  handleDropChange(
    headers: HeaderCellConfiguration[],
    event: any,
  ): HeaderCellConfiguration[] {
    moveItemInArray(headers, event.previousIndex, event.currentIndex);
    return headers;
  }

  handleSortChange(
    documentTypeColumns: HeaderCellConfiguration[],
    event: WcgTableSortBy,
  ): { columns: HeaderCellConfiguration[]; sortParam: any } {
    const columns = this.updateColumnSortState(documentTypeColumns, event);
    const sortParam = this.updateSortParam(event, columns);
    return { columns, sortParam };
  }

  handlePageEvent(event: PageEvent): { pageSize: number; pageIndex: number } {
    return { pageSize: event.pageSize, pageIndex: event.pageIndex };
  }

  mapFilterEvent(event: WcgAdvancedFilter[]) {
    const advancedFilters = event?.map((item) => {
      const [column, nameObj] = Object.entries(item)[0] as [
        string,
        WcgAdvancedFilterOptions,
      ];

      const { value, type, formatted, columnKey } = nameObj;

      let values;
      if (type === 'date-range' && formatted) {
        if (!nameObj.end) {
          nameObj.start = '';
        }
        values = nameObj;
      } else {
        values = value;
      }

      return {
        column: columnKey || column,
        type,
        values,
      };
    });

    return { advancedFilters };
  }

  mapFilterToTableData(event: WcgAdvancedFilter[], tableData: WcgTableData) {
    const mapedEvent = this.mapFilterEvent(event);
    return this.applyFiltersToTableData(tableData, mapedEvent);
  }

  mapAdvanceFilter(data: Record<string, unknown>): unknown[] {
    return Object.keys(data).map((key) => {
      return {
        [key]: data[key],
      };
    });
  }

  removeFilterFromTableData(
    event: WcgFilterIndicatorsOptions,
    filter: WcgAdvancedFilter[],
    tableData: WcgTableData,
  ) {
    this._loadingService.showLoading();
    const updatedFilter = this.updateFilter(event, filter);
    const updatedTableData = this.updateTableDataAdvacedFilter(
      tableData,
      event,
    );
    return {
      filter: updatedFilter,
      tableData: updatedTableData,
    };
  }

  updateFilter(
    filterToRemove: WcgFilterIndicatorsOptions,
    filter: WcgAdvancedFilter[],
  ): WcgAdvancedFilter[] {
    const nextFilter = cloneDeep(filter);
    const column = Object.keys(filterToRemove).toString();

    if (nextFilter.length === 0) return nextFilter;

    const filterObject = nextFilter.find((f) => f[column]);

    if (!filterObject) return nextFilter;

    const filterDetails = filterObject[column];
    let values: (WcgMenuOption | string)[] = [];
    switch (this._removeDashesAndUnderscores(filterDetails.type!)) {
      case 'string':
        filterObject[column] = {
          ...filterDetails,
          value: '',
        };
        break;
      case 'daterange':
        filterObject[column] = {
          ...filterDetails,
          formatted: '',
          start: '',
          end: '',
        };
        break;
      case 'singleselect':
        filterObject[column] = {
          ...filterDetails,
          value: '',
        };
        break;
      case 'multiselect':
        values = [
          ...this.filterOutMultiSelectValue(
            filterDetails.value as (WcgMenuOption | string)[],
            filterToRemove,
            column,
          ),
        ];
        filterObject[column] = {
          ...filterDetails,
          value: values.every((val) => typeof val === 'string')
            ? (values as string[])
            : (values as WcgMenuOption[]),
        };
        break;
      default:
        filterObject[column] = {
          ...filterDetails,
          value: '',
        };
    }

    return nextFilter;
  }

  updateTableDataAdvacedFilter(
    tableData: WcgTableData,
    filterToRemove: WcgFilterIndicatorsOptions,
  ) {
    const nextTableData = cloneDeep(tableData);
    const column = Object.keys(filterToRemove).toString();

    if (!nextTableData.filters?.advancedFilters?.[column]) return nextTableData;

    const filter = nextTableData.filters.advancedFilters[column];
    let values: (WcgMenuOption | string)[] = [];
    switch (filter.type) {
      case 'string':
        nextTableData.filters.advancedFilters[column] = {
          ...filter,
          value: '',
        };
        break;
      case 'date-range':
      case 'date_range':
        nextTableData.filters.advancedFilters[column] = {
          ...nextTableData.filters.advancedFilters[column],
          formatted: '',
          start: '',
          end: '',
        };
        break;
      case 'single-select':
      case 'single_select':
        nextTableData.filters.advancedFilters[column] = {
          ...filter,
          value: '',
        };
        break;
      case 'multi-select':
      case 'multi_select':
        values = [
          ...this.filterOutMultiSelectValue(
            filter.value as (WcgMenuOption | string)[],
            filterToRemove,
            column,
          ),
        ];
        nextTableData.filters.advancedFilters[column] = {
          ...filter,
          value: values,
        };

        break;
      default:
        nextTableData.filters.advancedFilters[column] = {
          ...filter,
          value: '',
        };
    }

    this._store.dispatch(
      GRID_VIEW_DATA_ACTIONS.setCustomFilters({
        filters: nextTableData.filters,
        sortBy: tableData.sortBy!,
      }),
    );

    return nextTableData;
  }

  filterOutMultiSelectValue(
    values: (WcgMenuOption | string)[],
    filterToRemove: WcgFilterIndicatorsOptions,
    column: string,
  ): (WcgMenuOption | string)[] {
    return values.filter((val) => {
      if (typeof val === 'string') {
        const removeVal = filterToRemove[column] as string;
        return val !== removeVal;
      }
      const removeVal = (filterToRemove[column] as WcgAdvancedFilterOptions)
        .value;
      return val.value !== removeVal;
    });
  }

  getDocumentTypeFilters(responseData: DocumentTypeFilter[]) {
    return responseData
      .filter((doc) => !!doc.name && !!doc.id)
      .reduce((acc, curr) => {
        if (!acc.some((ac) => ac.name === curr.name)) {
          acc.push({
            name: curr.name,
            id: curr.id,
          });
        }
        return acc;
      }, [] as DocumentTypeFilter[]);
  }

  private _removeDashesAndUnderscores(key: string) {
    return key.replace(/[_|-]/g, '');
  }

  getWorkspaceSitesUrl(submissionId: string): string {
    return `${environment.irisApi}/workspace/${submissionId}/sites/`;
  }

  getStudySiteContactUrl(
    studyId: string,
    entityType: string,
    siteId?: string,
  ): string {
    return `${environment.irisApi}/study/${studyId}/contact/?team=client&level=${entityType}${entityType === 'site' && siteId ? `&site_id=${siteId}` : ''}`;
  }

  getAdminUserUrl(viewType: string): string {
    return `${environment.irisApi}/auth/admin/user/?is_internal=${viewType === 'WCG'}`;
  }

  getUserNotificationSettingsUrl(): string {
    return `${environment.irisApi}/user/notification/settings/`;
  }

  getAdminQuestionnaireUrl(): string {
    return `${environment.irisApi}/admin/questionnaire/`;
  }

  getDocumentsUrl(type: string, id: string): string {
    return `${environment.irisApi}/${type}/${id}/documents/`;
  }

  getSiteOutcomeDocumentsUrl(id: string): string {
    return `${environment.irisApi}/site/${id}/documents/?is_outcome=true`;
  }

  getStudyOutcomeDocumentsUrl(id: string): string {
    return `${environment.irisApi}/study/${id}/outcome-documents/`;
  }

  getSiteLocationUrl(siteId: string): string {
    return `${environment.irisApi}/site/${siteId}/location/`;
  }

  getSiteUrl(): string {
    return `${environment.irisApi}/site/`;
  }

  getStudyUrl(): string {
    return `${environment.irisApi}/study/`;
  }

  getStudySiteUrl(studyId: string): string {
    return `${environment.irisApi}/study/${studyId}/site/`;
  }

  getWorkspaceUrl(): string {
    return `${environment.irisApi}/workspace/`;
  }

  getWorkspaceDocumentUrl(submissionId: string): string {
    return `${environment.irisApi}/document/workspace/${submissionId}/`;
  }

  getWorkspaceOutcomeDocumentUrl(submissionId: string): string {
    return `${environment.irisApi}/workspace/${submissionId}/outcome-documents/`;
  }

  getStudyContactUrl(studyId: string): string {
    return `${environment.irisApi}/study/${studyId}/contact/`;
  }

  getStudySiteWorkspaceUrl(id: string, site: boolean): string {
    return site
      ? `${environment.irisApi}/site/${id}/workspace/`
      : `${environment.irisApi}/study/${id}/workspaces/`;
  }
}
